import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchVimeoMetadata } from '../../hooks/api/useBrowseExperiences';
import { usePeersonaIntroVideo, VideoDimensions } from '../../hooks/utils/usePeersonaIntroVideo';
import { parseVimeoEmbedRef, upscaleVimeoThumbnailUrl, VimeoEmbedRef } from '../../utils/vimeo-embed-ref';
import { PeersonaAvatar } from './peersona-avatar';

/** Shared frame width — welcome hero and About drawer must match to avoid reflow on toggle. */
const FRAME_WIDTH_CLASS = 'w-[226px] max-w-full';
const FALLBACK_AVATAR_SIZE = 140;

interface VideoMetadata {
  thumbnailUrl: string | null;
  videoDimensions: VideoDimensions | null;
}

const EMPTY_METADATA: VideoMetadata = { thumbnailUrl: null, videoDimensions: null };

async function fetchPeersonaIntroMetadata(embedRef: VimeoEmbedRef): Promise<VideoMetadata> {
  const meta = await fetchVimeoMetadata(embedRef.videoId, embedRef.privacyHash);
  if (!meta.thumbnailUrl || !meta.width || !meta.height) {
    return { thumbnailUrl: meta.thumbnailUrl, videoDimensions: null };
  }
  return {
    thumbnailUrl: upscaleVimeoThumbnailUrl(meta.thumbnailUrl, meta.width, meta.height),
    videoDimensions: { width: meta.width, height: meta.height },
  };
}

interface PeersonaIntroVideoProps {
  peerName: string;
  welcomeVideoVimeoId?: string | null;
  avatar?: string | null;
}

const CenterSpinner: React.FC = () => (
  <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden />
);

interface VideoOverlayProps {
  peerName: string;
  mode: 'play' | 'pause' | 'loading';
  onClick: () => void;
}

const VideoOverlay: React.FC<VideoOverlayProps> = ({ peerName, mode, onClick }) => {
  const label =
    mode === 'play'
      ? `Play intro from ${peerName}`
      : mode === 'pause'
        ? `Pause intro from ${peerName}`
        : `Loading intro from ${peerName}`;

  return (
    <button
      type="button"
      aria-label={label}
      onClick={mode === 'loading' ? undefined : onClick}
      disabled={mode === 'loading'}
      className={[
        'absolute inset-0 z-[2] flex items-center justify-center transition-colors',
        mode === 'loading' ? 'bg-black/25' : 'bg-black/10 hover:bg-black/15',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300',
      ].join(' ')}
    >
      {mode === 'loading' ? (
        <CenterSpinner />
      ) : (
        <span
          className="grid h-[45px] w-[45px] place-items-center rounded-full bg-white/90 text-indigo-900 shadow-md"
          aria-hidden
        >
          {mode === 'play' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
            </svg>
          )}
        </span>
      )}
    </button>
  );
};

/**
 * Peersona intro — Vimeo poster, then lazy player on tap. The frame's aspect
 * ratio is set from the video's true dimensions (oEmbed first, then refined
 * by the player once ready) so the iframe plays without pillarbox bars.
 * Falls back to `PeersonaAvatar` when no vimeo id is set.
 */
export const PeersonaIntroVideo: React.FC<PeersonaIntroVideoProps> = ({ peerName, welcomeVideoVimeoId, avatar }) => {
  const embedRef = useMemo(() => parseVimeoEmbedRef(welcomeVideoVimeoId), [welcomeVideoVimeoId]);
  const [activated, setActivated] = useState(false);
  const [metadata, setMetadata] = useState<VideoMetadata>(EMPTY_METADATA);
  const {
    containerRef,
    phase,
    togglePlayPause,
    videoDimensions: playerDimensions,
  } = usePeersonaIntroVideo(welcomeVideoVimeoId, activated);

  useEffect(() => {
    if (!embedRef) {
      setMetadata(EMPTY_METADATA);
      return;
    }
    let cancelled = false;
    fetchPeersonaIntroMetadata(embedRef).then((meta) => {
      if (!cancelled) setMetadata(meta);
    });
    return () => {
      cancelled = true;
    };
  }, [embedRef]);

  const handleOverlayClick = useCallback(() => {
    if (!activated) {
      setActivated(true);
      return;
    }
    togglePlayPause();
  }, [activated, togglePlayPause]);

  if (!embedRef) {
    return <PeersonaAvatar size={FALLBACK_AVATAR_SIZE} src={avatar} name={peerName} />;
  }

  const dimensions = playerDimensions ?? metadata.videoDimensions;
  const overlayMode = phase === 'loading' ? 'loading' : phase === 'playing' ? 'pause' : 'play';

  return (
    <div
      style={dimensions ? { aspectRatio: `${dimensions.width} / ${dimensions.height}` } : undefined}
      className={[
        'relative overflow-hidden rounded-[16px] bg-black',
        FRAME_WIDTH_CLASS,
        dimensions ? '' : 'aspect-[226/314]',
      ].join(' ')}
    >
      {metadata.thumbnailUrl ? (
        <img
          src={metadata.thumbnailUrl}
          alt=""
          className={[
            'absolute inset-0 h-full w-full object-cover',
            phase === 'playing' ? 'opacity-0' : 'opacity-100',
          ].join(' ')}
        />
      ) : null}
      <div
        ref={containerRef}
        className={[
          'absolute inset-0 origin-center',
          // Slight overscan hides Vimeo's internal pillarbox until the video element fills.
          phase === 'playing' ? 'scale-[1.04]' : '',
          '[&_iframe]:!absolute [&_iframe]:!inset-0 [&_iframe]:!h-full [&_iframe]:!w-full',
          phase === 'playing' ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />
      {overlayMode !== 'pause' ? (
        <VideoOverlay peerName={peerName} mode={overlayMode} onClick={handleOverlayClick} />
      ) : (
        <button
          type="button"
          aria-label={`Pause intro from ${peerName}`}
          onClick={handleOverlayClick}
          className="absolute inset-0 z-[2] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
        />
      )}
    </div>
  );
};
